import axios from 'axios';
import * as Sentry from '@sentry/react';
import { getHttpErrorMessage, getHttpErrorStatus } from '@/lib/httpErrorMessage';
import type { ProfileDocumentKind } from '@paladin-monorepo/common';

export const PROFILE_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

export const PROFILE_DOCUMENT_ALLOWED_MIMES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
] as const;

const PROFILE_DOCUMENT_ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.webp'];

export type ProfileDocumentUploadValidation =
  | { ok: true }
  | { ok: false; messageKey: 'profile.credentials.fileTooLarge' | 'profile.credentials.fileTypeNotAllowed' };

export function validateProfileDocumentFile(file: File): ProfileDocumentUploadValidation {
  if (file.size > PROFILE_DOCUMENT_MAX_BYTES) {
    return { ok: false, messageKey: 'profile.credentials.fileTooLarge' };
  }

  const mimeAllowed = PROFILE_DOCUMENT_ALLOWED_MIMES.includes(
    file.type as (typeof PROFILE_DOCUMENT_ALLOWED_MIMES)[number],
  );
  const extension = file.name.includes('.')
    ? file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
    : '';
  const extensionAllowed = PROFILE_DOCUMENT_ALLOWED_EXTENSIONS.includes(extension);

  if (!mimeAllowed && !extensionAllowed) {
    return { ok: false, messageKey: 'profile.credentials.fileTypeNotAllowed' };
  }

  return { ok: true };
}

type UploadErrorMessageKey =
  | 'profile.credentials.fileTooLarge'
  | 'profile.credentials.fileTypeNotAllowed'
  | 'profile.credentials.uploadUnexpectedError';

export function resolveProfileDocumentUploadError(
  error: unknown,
): { messageKey: UploadErrorMessageKey; reportToSentry: boolean } {
  const status = getHttpErrorStatus(error);

  if (status === 413) {
    return { messageKey: 'profile.credentials.fileTooLarge', reportToSentry: false };
  }

  if (status === 400) {
    const detail = getHttpErrorMessage(error, '').toLowerCase();
    if (detail.includes('10mb') || detail.includes('size')) {
      return { messageKey: 'profile.credentials.fileTooLarge', reportToSentry: false };
    }
    if (detail.includes('type') || detail.includes('allowed') || detail.includes('validation failed')) {
      return { messageKey: 'profile.credentials.fileTypeNotAllowed', reportToSentry: false };
    }
  }

  if (!axios.isAxiosError(error) || !error.response || status == null || status >= 500) {
    return { messageKey: 'profile.credentials.uploadUnexpectedError', reportToSentry: true };
  }

  if (status === 400 || status === 403 || status === 404) {
    return { messageKey: 'profile.credentials.uploadUnexpectedError', reportToSentry: true };
  }

  return { messageKey: 'profile.credentials.uploadUnexpectedError', reportToSentry: true };
}

export function reportProfileDocumentUploadError(
  error: unknown,
  context: { kind: ProfileDocumentKind; fileName?: string; fileSize?: number; fileType?: string },
): void {
  Sentry.captureException(error, {
    tags: { feature: 'profile-document-upload' },
    extra: {
      kind: context.kind,
      fileName: context.fileName,
      fileSize: context.fileSize,
      fileType: context.fileType,
      status: getHttpErrorStatus(error),
    },
  });
}
